import { Component, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { TpaService } from '../../services/tpa.service';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
  MatOption,
} from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TpaSearchForm, TpaSearchFormated } from '../../models/tpa-search.model';
import { MatSelect } from '@angular/material/select';
import { SearchDisplayPipe } from '../../pipes/search-display.pipe';
import { DHIG_DATE_FORMATS, DhigDateAdapter } from '../../../../adapters/dhig-date.adapter';
import { TpaHttpService } from '../../services/tpa-http.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { TpaSearchCoverageRes, TpaSearchCoverageResExtended } from '../../models/http/tpa-http.model';
import { MatDialog } from '@angular/material/dialog';
import { PersonSelectionModalComponent } from './components/person-selection-modal/person-selection-modal.component';

@Component({
  selector: 'app-tpa-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatSelect,
    MatOption,
    SearchDisplayPipe,
    MatProgressBar,
  ],
  providers: [
    DatePipe,
    { provide: DateAdapter, useClass: DhigDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DHIG_DATE_FORMATS },
  ],
  templateUrl: './tpa-search.component.html',
  styleUrl: './tpa-search.component.scss',
})
export class TpaSearchComponent implements OnInit, OnDestroy {
  @Output() search = new Subject<TpaSearchFormated>();

  searchForm: FormGroup = {} as FormGroup;
  errorMessages: string[] = [];
  lastSearches: TpaSearchForm[] = [];
  selectedSearch: TpaSearchForm | null = null;
  isLoading = false;

  private unsubscribe$ = new Subject<void>();

  private tpaService = inject(TpaService);
  private fb = inject(FormBuilder);
  private datePipe = inject(DatePipe);
  private tpaHttpService = inject(TpaHttpService);
  private dialog = inject(MatDialog);

  ngOnInit() {
    this.tpaService.setTitle('Coverage Check');
    this.initiateForm();
    this.populateLastSearchData();
  }

  onSubmit() {
    const search: TpaSearchForm = this.searchForm.value;
    const searchFormatted: TpaSearchFormated = {
      ...search,
      dateOfBirth: this.datePipe.transform(search.dateOfBirth, 'yyyy-MM-dd'),
    };

    if (this.isFormValid()) {
      this.tpaService.saveToLocalStorage(searchFormatted);
      this.populateLastSearchData();
      this.isLoading = true;

      this.tpaHttpService
        .tpaSearchMember(searchFormatted)
        .pipe(
          takeUntil(this.unsubscribe$),
          finalize(() => (this.isLoading = false)),
        )
        .subscribe((data) => {
          if (this.containMultiplePersons(data)) {
            this.openPersonSelectionModal(data);
            return;
          }
          this.tpaService.setSearchResults(data);
          this.tpaService.tpaNavigateTo('details');
        });
    }
  }

  onSearchSelect(selection: TpaSearchForm): void {
    this.selectedSearch = selection;

    if (!selection) {
      return;
    }

    this.searchForm.patchValue({
      firstName: selection.firstName,
      lastName: selection.lastName,
      dateOfBirth: selection.dateOfBirth,
      memberId: selection.memberId,
      policyNumber: selection.policyNumber,
      externalId: selection.externalId || '',
    });
  }

  clearForm(): void {
    this.selectedSearch = null;
    this.searchForm.reset();
  }

  trackBySearch(index: number, search: TpaSearchForm): string {
    return `${search.firstName}_${search.lastName}_${search.memberId}_${search.policyNumber}_${search.dateOfBirth || ''}_${search.externalId || ''}_${index}`;
  }

  private initiateForm() {
    this.searchForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      dateOfBirth: [''],
      memberId: [''],
      policyNumber: [''],
      externalId: [''],
    });

    this.searchForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.errorMessages = []; // Clear error messages when form values change
    });
  }

  private isFormValid(): boolean {
    const form = this.searchForm.value;

    if (form.firstName && form.lastName && form.dateOfBirth) {
      return true;
    }

    if (form.firstName && form.lastName && (form.memberId || form.policyNumber || form.externalId)) {
      return true;
    }

    if (form.dateOfBirth && (form.memberId || form.policyNumber || form.externalId)) {
      return true;
    }

    this.setErrorMessages(form);
    return false;
  }

  private setErrorMessages(form: any) {
    this.errorMessages = [];

    if (form.firstName && !form.lastName) {
      this.errorMessages.push('Last Name is required when First Name is provided.');
    }

    if (!form.firstName && form.lastName) {
      this.errorMessages.push('First Name is required when Last Name is provided.');
    }

    this.errorMessages.push(
      'At least two of the following must be provided: First Name and Last Name, Date Of Birth, Member ID or Policy Number, or Alternative ID',
    );
  }

  private populateLastSearchData() {
    this.lastSearches = this.tpaService.getFromLocalStorage();
  }

  private containMultiplePersons(data: TpaSearchCoverageRes[]): boolean {
    const personIds = new Set(data.map((item) => item.personId));
    return personIds.size > 1;
  }

  openPersonSelectionModal(data: any) {
    const dialogRef = this.dialog.open(PersonSelectionModalComponent, {
      data:  data,
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        let personCoverage: TpaSearchCoverageResExtended[] = data.filter(
          (item: TpaSearchCoverageResExtended) => item.personId === result
        );
        this.tpaService.setSearchResults(personCoverage);
        this.tpaService.tpaNavigateTo('details');
      }
    });
  }


  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
